<template>
    <span
        v-if="hasScore"
        class="lead-score-badge"
        :class="'lead-score-' + typeSlug"
        :title="displayLabel + ' (' + score + ')'"
    >
        <i class="lead-score-icon" :class="iconClass"></i>
        <span class="lead-score-text">
            <strong>{{ score }}</strong>
            <span class="lead-score-type">{{ displayType }}</span>
        </span>
    </span>
</template>

<script>
export default {
    name: 'LeadScoreBadge',
    props: {
        score: {
            type: [Number, String],
            default: null,
        },
        type: {
            type: String,
            default: null,
        },
        label: {
            type: String,
            default: null,
        },
    },
    computed: {
        hasScore() {
            return this.score !== null && this.score !== undefined && this.score !== '';
        },
        resolvedType() {
            if (this.type) {
                return String(this.type).replace(/^AI\s+/i, '');
            }
            const score = Number(this.score);
            if (Number.isNaN(score)) {
                return null;
            }
            if (score >= 50) {
                return 'Hot';
            }
            if (score >= 20) {
                return 'Warm';
            }
            return 'Cold';
        },
        typeSlug() {
            return String(this.resolvedType || '').toLowerCase();
        },
        iconClass() {
            if (this.resolvedType === 'Hot') {
                return 'fa-solid fa-fire';
            }
            if (this.resolvedType === 'Warm') {
                return 'fa-solid fa-sun';
            }
            return 'fa-solid fa-snowflake';
        },
        displayType() {
            return this.resolvedType ? 'AI ' + this.resolvedType : '';
        },
        displayLabel() {
            return this.label || this.displayType;
        },
    },
};
</script>

<style scoped>
.lead-score-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.4;
    vertical-align: middle;
    white-space: nowrap;
    border: 1px solid transparent;
}

.lead-score-icon {
    font-size: 13px;
    animation: lead-score-pulse 1.6s ease-in-out infinite;
}

.lead-score-text {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
}

.lead-score-type {
    font-weight: 500;
    opacity: 0.95;
}

.lead-score-hot {
    color: #fff;
    background: linear-gradient(135deg, #ff4d4f 0%, #ff7a45 100%);
    box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.45);
    animation: lead-score-glow-hot 1.8s ease-in-out infinite;
}

.lead-score-hot .lead-score-icon {
    animation: lead-score-flicker 0.9s ease-in-out infinite;
}

.lead-score-warm {
    color: #7a3e00;
    background: linear-gradient(135deg, #ffe58f 0%, #ffc069 100%);
    border-color: #ffc069;
}

.lead-score-warm .lead-score-icon {
    animation: lead-score-spin-soft 3.2s linear infinite;
    color: #d46b08;
}

.lead-score-cold {
    color: #003a8c;
    background: linear-gradient(135deg, #bae7ff 0%, #69c0ff 100%);
    border-color: #69c0ff;
}

.lead-score-cold .lead-score-icon {
    animation: lead-score-float 2.2s ease-in-out infinite;
    color: #096dd9;
}

@keyframes lead-score-pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.12); }
}

@keyframes lead-score-flicker {
    0%, 100% { transform: scale(1) rotate(-4deg); opacity: 1; }
    50% { transform: scale(1.2) rotate(4deg); opacity: 0.85; }
}

@keyframes lead-score-glow-hot {
    0%, 100% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.4); }
    50% { box-shadow: 0 0 10px 2px rgba(255, 122, 69, 0.55); }
}

@keyframes lead-score-spin-soft {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

@keyframes lead-score-float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-2px); }
}
</style>
